import React, { useState, useEffect } from "react";
import { EsBadge } from "../components/EsBadge";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pill, Pencil, Trash2, Clock, StickyNote, ListChecks, Printer, ChevronDown } from "lucide-react";
import OspiteAvatar from "../components/OspiteAvatar";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFirma, OperatoreLocked } from "../context/FirmaContext";
import { Button } from "../components/ui/button";
import { ExportButton } from "../components/ExportButton";
import { AvvisoOSS } from "../components/AvvisoOSS";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { TerapiaOrari } from "../components/TerapiaOrari";
import { TerapieTabella } from "../components/TerapieTabella";
import { useVistaPredefinita } from "../lib/vistePredefinite";

const dedupeOrari = (s) => [...new Set((s || "").split(",").map((x) => x.trim()).filter(Boolean))].sort().join(", ");
const GIORNI = [["lun", "Lun"], ["mar", "Mar"], ["mer", "Mer"], ["gio", "Gio"], ["ven", "Ven"], ["sab", "Sab"], ["dom", "Dom"]];
const giorniTxt = (t) => { const g = t.giorni || []; if (!g.length || g.length === 7) return ""; const q = t.quantita_giorni || {}; return GIORNI.filter(([c]) => g.includes(c)).map(([c, l]) => (q[c] ? `${l} ×${q[c]}` : l)).join(", "); };
const qtaGiorno = (t, code, ora) => { const es = (t.schema_giorni || {})[code]; if (es && ora) return es.find((e) => e.ora === ora)?.qta || ""; return (t.quantita_giorni || {})[code] || t.quantita || ""; };
const doseGiorno = (t, code, ora) => ((t.schema_giorni || {})[code] || []).find((e) => e.ora === ora)?.dose || t.dosaggio || "";
const todayGiorno = () => ["dom", "lun", "mar", "mer", "gio", "ven", "sab"][new Date().getDay()];

export default function Terapie() {
  const { selectedStructure, structures } = useAuth();
  const { opName } = useFirma();
  const nav = useNavigate();
  const qc = useQueryClient();
  const params = { structure_id: selectedStructure };
  const refreshTer = () => { qc.invalidateQueries({ queryKey: ["terapie"] }); qc.invalidateQueries({ queryKey: ["ter-storico"] }); };
  const { data: ospiti = [] } = useQuery({ queryKey: ["ospiti-ter", selectedStructure], queryFn: async () => (await api.get("/ospiti", { params })).data });
  const { data: terapie = [] } = useQuery({ queryKey: ["terapie", selectedStructure], queryFn: async () => (await api.get("/terapie", { params })).data });
  const { data: evTer = [] } = useQuery({ queryKey: ["inf-terapia", "tutte", selectedStructure], queryFn: async () => (await api.get("/terapie/evento-attive", { params })).data });
  const [dlg, setDlg] = useState(null);
  const [delDlg, setDelDlg] = useState(null);
  const [operatori, setOperatori] = useState([]);
  const [form, setForm] = useState({});
  const [oraTemp, setOraTemp] = useState("");
  const [search, setSearch] = useState("");
  const [tipoF, setTipoF] = useState("tutte");
  const [statoF, setStatoF] = useState("attiva");
  const [giroDlg, setGiroDlg] = useState(false);
  const [giroOra, setGiroOra] = useState(null);
  const [showDimessi, setShowDimessi] = useState(false);
  const [giorniMode, setGiorniMode] = useState("tutti");
  const [vistaT, setVistaT, predT] = useVistaPredefinita("terapie", "terapie-vista", "attuale");

  useEffect(() => {
    const o = dlg?.ospite || delDlg?.ospite;
    if (o) api.get("/firma/operatori", { params: { structure_id: o.structure_id } }).then((r) => setOperatori(r.data));
  }, [dlg, delDlg]);

  const list = (oid, tipo) => terapie.filter((t) => t.ospite_id === oid && t.tipo === tipo && (statoF === "tutte" ? true : t.stato === statoF));
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const giorniArr = form.giorni || [];
  const toggleGiorno = (code) => setF("giorni", giorniArr.includes(code) ? giorniArr.filter((x) => x !== code) : [...giorniArr, code]);
  const orariArr = (form.orari || "").split(",").map((x) => x.trim()).filter(Boolean);
  const commitOra = (val) => { const v = (val || "").trim(); if (!/^\d{1,2}:\d{2}$/.test(v)) return; setF("orari", [...new Set([...orariArr, v])].sort().join(", ")); setOraTemp(""); };
  const removeOra = (o) => setF("orari", orariArr.filter((x) => x !== o).join(", "));

  const vaiCartella = (o) => nav(`/impostazione-terapie?ospite=${o.id}`);
  const openNew = (o) => vaiCartella(o);
  const openEdit = (o) => vaiCartella(o);
  const save = async () => {
    if (!form.farmaco || !form.farmaco.trim()) { toast.error("Inserisci il farmaco."); return; }
    if (!form.operatore_nome) { toast.error("Seleziona l'operatore che effettua la modifica."); return; }
    const o = dlg.ospite;
    const qg = Object.fromEntries(Object.entries(form.quantita_giorni || {}).filter(([c, v]) => (form.giorni || []).includes(c) && String(v || "").trim()));
    const payload = { ...form, quantita_giorni: qg, ospite_id: o.id, ospite_nome: `${o.nome} ${o.cognome}`, structure_id: o.structure_id };
    try {
      if (dlg.existing?.id) await api.put(`/terapie/${dlg.existing.id}`, payload);
      else await api.post("/terapie", payload);
      toast.success("Terapia salvata"); setDlg(null); refreshTer();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const openDel = (o, t) => { setDelDlg({ ospite: o, terapia: t, operatore_nome: opName || "" }); };
  const confirmDel = async () => {
    if (!delDlg.operatore_nome) { toast.error("Seleziona l'operatore che effettua la rimozione."); return; }
    try {
      await api.put(`/terapie/${delDlg.terapia.id}`, { operatore_nome: delDlg.operatore_nome });
      await api.delete(`/terapie/${delDlg.terapia.id}`);
      toast.success("Terapia eliminata"); setDelDlg(null); refreshTer();
    } catch { toast.error("Errore"); }
  };
  const byTime = (items) => {
    const map = {}; const noTime = [];
    items.forEach((t) => {
      const times = [...new Set((t.orari || "").split(",").map((x) => x.trim()).filter(Boolean))];
      if (!times.length) noTime.push(t); else times.forEach((tm) => { (map[tm] = map[tm] || []).push(t); });
    });
    return { keys: Object.keys(map).sort(), map, noTime };
  };
  const medRow = (o, t) => (
    <div key={t.id} data-testid={`ter-${t.id}`} className="group flex items-start justify-between gap-1.5">
      <button onClick={() => openEdit(o, t)} data-testid={`ter-edit-${t.id}`} className="min-w-0 text-left flex-1">
        <div className="break-words">
          <span className="text-xs font-semibold">{t.farmaco}</span>
          {t.generico && <span className="text-[11px] italic text-muted-foreground"> ({t.generico})</span>}
          {t.dosaggio && <span className="text-[11px] text-muted-foreground"> · {t.dosaggio}</span>}
          {t.quantita && <span className="text-[11px] text-sky-700 font-medium"> · Q.tà {t.quantita}</span>}
          {[t.via, t.pasti, t.ogni_giorni ? `ogni ${t.ogni_giorni} gg` : "", t.dal || t.al ? `${t.dal ? `dal ${t.dal.split("-").reverse().join("/")}` : ""}${t.al ? ` al ${t.al.split("-").reverse().join("/")}` : " · senza fine"}` : ""].filter(Boolean).map((x) => <span key={x} className="text-[11px] text-muted-foreground"> · {x}</span>)}
          {t.stato && t.stato !== "attiva" && <span className="text-[11px] text-muted-foreground capitalize"> · {t.stato}</span>}
          {giorniTxt(t) && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 rounded px-1 py-0.5 ml-1" data-testid={`ter-giorni-${t.id}`}>{giorniTxt(t)}</span>}
        </div>
        {t.note && t.note.trim() && (
          <div className="mt-0.5 flex items-start gap-1 text-[10px] text-amber-800" data-testid={`ter-note-${t.id}`}>
            <StickyNote size={9} className="mt-0.5 shrink-0" /><span className="line-clamp-2">{t.note}</span>
          </div>
        )}
      </button>
      <div className="flex items-center gap-1 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
        <Pencil size={11} className="text-muted-foreground cursor-pointer" onClick={() => openEdit(o, t)} />
      </div>
    </div>
  );

  const isDimesso = (o) => !!((o.data_dimissione && String(o.data_dimissione).trim()) || o.status === "eliminato");

  const ospiteBox = (o, num) => {
    const bisogno = list(o.id, "bisogno");
    const dim = isDimesso(o);
    return (
      <div key={o.id} className="gr-card p-3" data-testid={`terapia-box-${o.id}`}>
        <div className="flex items-start gap-2.5">
          {num != null && <span className="shrink-0 h-6 w-6 grid place-items-center rounded-full bg-primary/10 text-primary text-xs font-bold tabnum" data-testid={`ter-num-${o.id}`}>{num}</span>}
          <OspiteAvatar ospite={o} size={40} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <button onClick={() => nav(`/ospiti/${o.id}`)} data-testid={`ter-nome-${o.id}`} className="font-bold text-sm text-blue-700 hover:underline text-left truncate">{o.nome} {o.cognome}</button><EsBadge id={o.id} />
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${dim ? "bg-orange-100 text-orange-700" : "bg-emerald-100 text-emerald-700"}`} data-testid={`ter-badge-${o.id}`}>{dim ? "Dimesso" : "Presente"}</span>
              <span className="text-[11px] text-muted-foreground shrink-0">· Cam. {o.camera || "—"}</span>
              <button onClick={() => nav(`/terapie/ospite/${o.id}`)} data-testid={`storico-btn-${o.id}`} className="ml-auto text-[11px] font-semibold text-muted-foreground hover:text-primary hover:underline shrink-0">Storico</button>
            </div>
            {tipoF !== "ordinaria" && (
              <div className="mt-1.5">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700">Al bisogno</span>
                </div>
                {bisogno.length === 0
                  ? <span className="text-[11px] text-muted-foreground">Nessuna terapia al bisogno</span>
                  : <div className="space-y-1">
                      {[...bisogno].sort((a, b) => (a.farmaco || "").localeCompare(b.farmaco || "")).map((t) => (
                        <div key={t.id} data-testid={`ter-${t.id}`} className="group flex items-center justify-between gap-1.5 text-[11px] px-2 py-1 rounded bg-amber-50 border border-amber-100">
                          <button onClick={() => openEdit(o, t)} data-testid={`ter-edit-${t.id}`} className="min-w-0 text-left flex-1 truncate hover:underline font-semibold text-amber-900" title={`${t.farmaco} ${t.dosaggio || ""}`}>
                            {t.farmaco}{t.generico && <span className="font-normal italic text-amber-800/70"> ({t.generico})</span>}{t.dosaggio && <span className="font-normal text-amber-800/70"> · {t.dosaggio}</span>}{t.quantita && <span className="font-normal text-sky-700"> · Q.tà {t.quantita}</span>}{giorniTxt(t) && <span className="font-normal text-emerald-700"> · {giorniTxt(t)}</span>}
                          </button>
                        </div>
                      ))}
                    </div>}
              </div>
            )}
          </div>
        </div>

        {tipoF !== "bisogno" && (
          <div className="mt-2.5 pt-2.5 border-t">
            <TerapiaOrari terapie={list(o.id, "ordinaria")} bisogno={false} scope={o.id} onOpen={(t) => openEdit(o, t)} />
            <button onClick={() => openNew(o)} data-testid={`modifica-cartella-${o.id}`} className="mt-1.5 text-[11px] font-semibold text-primary hover:underline">Modifica in Impostazione terapie →</button>
          </div>
        )}
        {evTer.some((t) => t.ospite_id === o.id) && (
          <div className="mt-2.5 pt-2.5 border-t border-rose-200" data-testid={`terapia-evento-${o.id}`}>
            <div className="text-[11px] font-bold uppercase tracking-wide text-rose-700 mb-1">Terapia per evento sanitario</div>
            <TerapiaOrari terapie={evTer.filter((t) => t.ospite_id === o.id)} scope={`ev-${o.id}`} extra={(t) => (t.da_confermare ? <span className="text-[9px] font-bold uppercase rounded-full px-1.5 py-0.5 bg-amber-500 text-white">In attesa di ricetta</span> : null)} />
            <button onClick={() => nav("/infettive")} data-testid={`modifica-evento-${o.id}`} className="mt-1.5 text-[11px] font-semibold text-rose-700 hover:underline">Modifica in Eventi sanitari →</button>
          </div>
        )}
      </div>
    );
  };

  const todayCode = todayGiorno();
  const takenToday = (t) => {
    const oggiIso = new Date().toISOString().slice(0, 10);
    if (t.dal && t.dal > oggiIso) return false;
    if (t.al && t.al < oggiIso) return false;
    if (t.ogni_giorni) { const n = Number(t.ogni_giorni) || 1; const diff = Math.round((new Date(oggiIso) - new Date(t.dal || oggiIso)) / 86400000); return diff % n === 0; }
    const g = t.giorni || []; return !g.length || g.includes(todayCode);
  };
  const orariOggi = (t) => (t.schema_giorni && Object.keys(t.schema_giorni).length ? (t.schema_giorni[todayCode] || []).map((e) => e.ora) : (t.orari || "").split(",").map((x) => x.trim()).filter(Boolean));
  const giroTimes = [...new Set(terapie.filter((t) => t.tipo === "ordinaria" && t.stato === "attiva" && t.status !== "eliminato" && takenToday(t)).flatMap(orariOggi))].sort();
  const giroRows = (ora) => terapie
    .filter((t) => t.tipo === "ordinaria" && t.stato === "attiva" && t.status !== "eliminato" && takenToday(t) && orariOggi(t).includes(ora))
    .map((t) => ({ t, o: ospiti.find((x) => x.id === t.ospite_id) }))
    .filter((r) => r.o)
    .sort((a, b) => `${a.o.camera || "zzz"}`.localeCompare(`${b.o.camera || "zzz"}`, undefined, { numeric: true }) || `${a.o.cognome}`.localeCompare(`${b.o.cognome}`));
  const openGiro = () => { setGiroOra(giroTimes[0] || null); setGiroDlg(true); };
  const stampaGiro = (ora) => {
    if (!ora) return;
    const rows = giroRows(ora);
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const oggi = new Date().toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
    const body = rows.length === 0
      ? `<tr><td colspan="7" style="padding:12px;text-align:center;color:#666">Nessuna somministrazione in questa fascia.</td></tr>`
      : rows.map(({ t, o }) => `<tr>
          <td class="cam">${esc(o.camera || "—")}</td>
          <td>${esc(o.nome)} ${esc(o.cognome)}</td>
          <td><strong>${esc(t.farmaco)}</strong>${t.generico ? ` <em>(${esc(t.generico)})</em>` : ""}</td>
          <td>${esc(doseGiorno(t, todayCode, ora))}</td>
          <td>${esc(qtaGiorno(t, todayCode, ora))}</td>
          <td class="note">${t.note ? esc(t.note) : ""}</td>
          <td class="check"></td>
        </tr>`).join("");
    const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"><title>Giro medicine ${esc(ora)}</title>
      <style>
        *{font-family:Arial,Helvetica,sans-serif;box-sizing:border-box}
        body{margin:24px;color:#111}
        h1{font-size:20px;margin:0 0 2px}
        .sub{color:#555;font-size:13px;margin-bottom:16px}
        .ora{display:inline-block;background:#16a34a;color:#fff;padding:4px 12px;border-radius:20px;font-weight:bold;font-size:15px}
        table{width:100%;border-collapse:collapse;font-size:13px;margin-top:10px}
        th,td{border:1px solid #ccc;padding:8px;text-align:left;vertical-align:top}
        th{background:#f1f5f9;font-size:12px;text-transform:uppercase;letter-spacing:.03em}
        td.cam{font-weight:bold;white-space:nowrap}
        td.note{color:#92400e;font-size:12px}
        td.check{width:60px}
        @media print{body{margin:12mm}}
      </style></head><body>
      <h1>Giro medicine — <span class="ora">Ore ${esc(ora)}</span></h1>
      <div class="sub">${esc(oggi)} · ${rows.length} somministrazioni</div>
      <table><thead><tr><th>Camera/zona</th><th>Ospite</th><th>Farmaco</th><th>Dosaggio</th><th>Q.tà</th><th>Note</th><th>Fatto</th></tr></thead>
      <tbody>${body}</tbody></table>
      <script>window.onload=function(){window.print();}</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Abilita i popup per stampare."); return; }
    w.document.write(html); w.document.close();
  };

  const terColor = (structures || []).find((s) => s.id === selectedStructure)?.colore || "#1f7a5c";
  const terName = selectedStructure === "all" ? "Tutte le strutture" : ((structures || []).find((s) => s.id === selectedStructure)?.nome || "");
  const terExportRows = terapie
    .filter((t) => (tipoF === "tutte" || t.tipo === tipoF) && (statoF === "tutte" || t.stato === statoF))
    .map((t) => { const o = ospiti.find((x) => x.id === t.ospite_id); return { ospite: o ? `${o.cognome} ${o.nome}` : (t.ospite_nome || ""), camera: o?.camera || "", tipo: t.tipo === "ordinaria" ? "Quotidiana" : "Al bisogno", farmaco: t.farmaco || "", generico: t.generico || "", dosaggio: t.dosaggio || "", quantita: t.quantita || "", orari: t.orari || "", giorni: giorniTxt(t) || "Tutti i giorni", via: t.via || "", frequenza: t.frequenza || "", stato: t.stato || "", note: t.note || "" }; })
    .filter((r) => { const q = search.trim().toLowerCase(); return !q || r.ospite.toLowerCase().includes(q) || r.farmaco.toLowerCase().includes(q); })
    .sort((a, b) => a.ospite.localeCompare(b.ospite));
  const terCols = [
    { header: "Ospite", accessor: "ospite" },
    { header: "Camera/zona", accessor: "camera" },
    { header: "Tipo", accessor: "tipo" },
    { header: "Farmaco", accessor: "farmaco" },
    { header: "Generico / equivalente", accessor: "generico" },
    { header: "Dosaggio", accessor: "dosaggio" },
    { header: "Quantità", accessor: "quantita" },
    { header: "Orari", accessor: "orari" },
    { header: "Giorni", accessor: "giorni" },
    { header: "Via", accessor: "via" },
    { header: "Frequenza", accessor: "frequenza" },
    { header: "Stato", accessor: "stato" },
    { header: "Note", accessor: "note" },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><Pill size={22} /> Terapie</h1>
          <p className="text-sm text-muted-foreground">Un box per ospite · terapia quotidiana e al bisogno · per modificarle tocca un farmaco: si apre Impostazione terapie</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="flex flex-col items-start gap-1"><div className="inline-flex rounded-lg border overflow-hidden text-xs font-semibold" data-testid="ter-vista-switch">{[["attuale", "Vista attuale"], ["tabella", "Vista tabella (prova)"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setVistaT(k)} className={`px-3 py-2 whitespace-nowrap transition-colors ${vistaT === k ? "bg-primary text-white" : "bg-white hover:bg-secondary"}`} data-testid={`ter-vista-${k}`}>{l}</button>
          ))}</div>{predT}</div>
          <ExportButton filename="terapie" title="Terapie" subtitle={terName} columns={terCols} rows={terExportRows} color={terColor} testid="export-terapie" />
          <Button onClick={openGiro} data-testid="giro-medicine-btn" variant="outline" className="font-semibold"><ListChecks size={16} className="mr-1" /> Giro medicine</Button>
        </div>
      </div>
      <AvvisoOSS className="mb-5" />

      {ospiti.length === 0 && <div className="gr-card p-8 text-center text-muted-foreground">Nessun ospite per la struttura selezionata.</div>}

      {ospiti.length > 0 && (
        <div className="gr-card p-3 mb-4 flex flex-wrap items-center gap-3">
          <Input placeholder="Cerca ospite o farmaco..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-60" data-testid="ter-search" />
          <Select value={tipoF} onValueChange={setTipoF}>
            <SelectTrigger className="w-44" data-testid="ter-filter-tipo"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="tutte">Tutte le terapie</SelectItem><SelectItem value="ordinaria">Solo quotidiana</SelectItem><SelectItem value="bisogno">Solo al bisogno</SelectItem></SelectContent>
          </Select>
          <Select value={statoF} onValueChange={setStatoF}>
            <SelectTrigger className="w-40" data-testid="ter-filter-stato"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="attiva">Attive</SelectItem><SelectItem value="sospesa">Sospese</SelectItem><SelectItem value="conclusa">Concluse</SelectItem><SelectItem value="tutte">Tutti gli stati</SelectItem></SelectContent>
          </Select>
        </div>
      )}

      {(() => {
        const q = search.trim().toLowerCase();
        const match = (o) => !q || `${o.nome} ${o.cognome}`.toLowerCase().includes(q) || terapie.some((t) => t.ospite_id === o.id && (t.farmaco || "").toLowerCase().includes(q));
        const ospitiF = ospiti.filter(match);
        const attivi = ospitiF.filter((o) => !isDimesso(o));
        const dimessi = ospitiF.filter((o) => isDimesso(o));
        return vistaT === "tabella" ? <TerapieTabella ospiti={attivi} list={list} evTer={evTer} structures={structures} tipoF={tipoF} /> : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {ospitiF.length === 0 && <div className="text-sm text-muted-foreground">Nessun risultato per i filtri selezionati.</div>}
              {attivi.length === 0 && ospitiF.length > 0 && <div className="text-sm text-muted-foreground">Nessun ospite presente per i filtri selezionati.</div>}
              {attivi.map((o, i) => ospiteBox(o, i + 1))}
            </div>

            {dimessi.length > 0 && (
              <div className="mt-6" data-testid="dimessi-section">
                <button onClick={() => setShowDimessi((v) => !v)} data-testid="toggle-dimessi" className="w-full flex items-center gap-2 text-left px-3 py-2.5 rounded-lg bg-orange-50 border border-orange-200 hover:bg-orange-100 transition-colors">
                  <ChevronDown size={16} className={`text-orange-700 transition-transform ${showDimessi ? "" : "-rotate-90"}`} />
                  <span className="font-bold text-sm text-orange-800">Dimessi</span>
                  <span className="text-xs font-semibold text-orange-700 bg-orange-200 rounded-full px-2 py-0.5 tabnum">{dimessi.length}</span>
                </button>
                {showDimessi && (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mt-3">
                    {dimessi.map((o) => ospiteBox(o, null))}
                  </div>
                )}
              </div>
            )}
          </>
        );
      })()}

      <Dialog open={!!dlg} onOpenChange={(v) => { if (!v) setDlg(null); }}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dlg?.existing ? "Modifica terapia" : "Nuova terapia"}{dlg ? ` — ${dlg.ospite.nome} ${dlg.ospite.cognome}` : ""}</DialogTitle>
            <DialogDescription>Il farmaco è obbligatorio.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs font-semibold mb-1 block">Farmaco *</Label><Input value={form.farmaco || ""} onChange={(e) => setF("farmaco", e.target.value)} data-testid="ter-farmaco" /></div>
            <div className="col-span-2"><Label className="text-xs font-semibold mb-1 block">Generico / equivalente</Label><Input value={form.generico || ""} onChange={(e) => setF("generico", e.target.value)} placeholder="Es. Furosemide" data-testid="ter-generico" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Dosaggio</Label><Input value={form.dosaggio || ""} onChange={(e) => setF("dosaggio", e.target.value)} data-testid="ter-dosaggio" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Quantità</Label><Input value={giorniMode === "specifici" ? "" : (form.quantita || "")} disabled={giorniMode === "specifici"} placeholder={giorniMode === "specifici" ? "vedi giorni" : ""} onChange={(e) => setF("quantita", e.target.value)} data-testid="ter-quantita" /></div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setF("tipo", v)}>
                <SelectTrigger data-testid="ter-tipo"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ordinaria">Quotidiana</SelectItem><SelectItem value="bisogno">Al bisogno</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-semibold mb-1 block">Orari <span className="font-normal text-muted-foreground">— scegli un orario, viene aggiunto subito</span></Label>
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="ter-orari">
                {orariArr.length === 0 && <span className="text-xs text-muted-foreground">Nessun orario impostato</span>}
                {orariArr.map((o) => (
                  <span key={o} className="text-xs px-2 py-1 rounded bg-secondary font-semibold flex items-center gap-1 tabnum">{o}<button type="button" onClick={() => removeOra(o)} className="text-muted-foreground hover:text-destructive">×</button></span>
                ))}
              </div>
              <Input type="time" value={oraTemp} data-testid="ter-ora-input" className="w-36"
                onChange={(e) => { setOraTemp(e.target.value); commitOra(e.target.value); }}
                onBlur={(e) => commitOra(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitOra(e.currentTarget.value); } }} />
            </div>
            <div><Label className="text-xs font-semibold mb-1 block">Via</Label><Input value={form.via || ""} onChange={(e) => setF("via", e.target.value)} /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Frequenza</Label><Input value={form.frequenza || ""} onChange={(e) => setF("frequenza", e.target.value)} /></div>
            <div className="col-span-2">
              <Label className="text-xs font-semibold mb-1 block">Quando prenderla</Label>
              <div className="flex gap-2 mb-2" data-testid="ter-giorni-mode">
                <button type="button" onClick={() => { setGiorniMode("tutti"); setF("giorni", []); }} data-testid="ter-giorni-tutti"
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${giorniMode === "tutti" ? "bg-primary text-primary-foreground border-primary" : "bg-secondary hover:bg-secondary/70 border-transparent"}`}>Tutti i giorni</button>
                <button type="button" onClick={() => setGiorniMode("specifici")} data-testid="ter-giorni-specifici"
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${giorniMode === "specifici" ? "bg-primary text-primary-foreground border-primary" : "bg-secondary hover:bg-secondary/70 border-transparent"}`}>Giorni specifici</button>
              </div>
              {giorniMode === "specifici" && (
                <div className="grid grid-cols-7 gap-1" data-testid="ter-giorni-picker">
                  {GIORNI.map(([c, l]) => { const on = giorniArr.includes(c); return (
                    <div key={c} className={`rounded-lg border overflow-hidden text-center ${on ? "border-emerald-500 bg-emerald-50" : "bg-white"}`}>
                      <button type="button" onClick={() => toggleGiorno(c)} data-testid={`ter-giorno-${c}`}
                        className={`w-full py-1.5 text-xs font-bold transition-colors ${on ? "bg-emerald-600 text-white" : "hover:bg-secondary"}`}>{l}</button>
                      <input value={(form.quantita_giorni || {})[c] || ""} disabled={!on} placeholder={on ? "Q.tà" : "—"} onChange={(e) => setF("quantita_giorni", { ...(form.quantita_giorni || {}), [c]: e.target.value })}
                        className="w-full h-7 text-center text-xs bg-transparent outline-none disabled:text-slate-300" data-testid={`ter-qta-giorno-${c}`} />
                    </div>); })}
                </div>
              )}
              {giorniMode === "specifici" && giorniArr.length === 0 && <span className="text-[11px] text-muted-foreground">Seleziona i giorni (nessuno = tutti i giorni)</span>}
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-semibold mb-1 block">Stato</Label>
              <Select value={form.stato} onValueChange={(v) => setF("stato", v)}>
                <SelectTrigger data-testid="ter-stato"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="attiva">Attiva</SelectItem><SelectItem value="sospesa">Sospesa</SelectItem><SelectItem value="conclusa">Conclusa</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-xs font-semibold mb-1 block">Note</Label><Textarea value={form.note || ""} onChange={(e) => setF("note", e.target.value)} /></div>
            <div className="col-span-2">
              <Label className="text-xs font-semibold mb-1 block">Operatore che effettua la modifica *</Label>
              {opName ? <OperatoreLocked name={opName} testid="ter-operatore" /> : (
              <Select value={form.operatore_nome || ""} onValueChange={(v) => setF("operatore_nome", v)}>
                <SelectTrigger data-testid="ter-operatore"><SelectValue placeholder="Seleziona operatore" /></SelectTrigger>
                <SelectContent>{operatori.map((op) => <SelectItem key={op.id} value={`${op.nome} ${op.cognome}`}>{op.nome} {op.cognome} ({op.qualifica})</SelectItem>)}</SelectContent>
              </Select>)}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button>
            <Button onClick={save} data-testid="ter-save" className="font-semibold">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delDlg} onOpenChange={(v) => { if (!v) setDelDlg(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Elimina terapia</DialogTitle>
            <DialogDescription>{delDlg ? `${delDlg.terapia.farmaco} — ${delDlg.ospite.nome} ${delDlg.ospite.cognome}. Indica chi effettua la rimozione.` : ""}</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs font-semibold mb-1 block">Operatore che rimuove *</Label>
            {opName ? <OperatoreLocked name={opName} testid="del-operatore" /> : (
            <Select value={delDlg?.operatore_nome || ""} onValueChange={(v) => setDelDlg((p) => ({ ...p, operatore_nome: v }))}>
              <SelectTrigger data-testid="del-operatore"><SelectValue placeholder="Seleziona operatore" /></SelectTrigger>
              <SelectContent>{operatori.map((op) => <SelectItem key={op.id} value={`${op.nome} ${op.cognome}`}>{op.nome} {op.cognome} ({op.qualifica})</SelectItem>)}</SelectContent>
            </Select>)}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDelDlg(null)}>Annulla</Button>
            <Button onClick={confirmDel} data-testid="confirm-del-terapia" className="font-semibold bg-destructive hover:bg-destructive/90">Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={giroDlg} onOpenChange={setGiroDlg}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ListChecks size={20} /> Giro medicine</DialogTitle>
            <DialogDescription>Scegli un orario: vengono elencati tutti gli ospiti e i farmaci quotidiani da somministrare in quella fascia.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="giro-times">
            {giroTimes.length === 0 && <span className="text-sm text-muted-foreground">Nessun orario impostato nelle terapie quotidiane.</span>}
            {giroTimes.map((tm) => (
              <button key={tm} onClick={() => setGiroOra(tm)} data-testid={`giro-ora-${tm}`}
                className={`text-sm px-3 py-1.5 rounded-full font-bold tabnum flex items-center gap-1 border transition-colors ${giroOra === tm ? "bg-primary text-primary-foreground border-primary" : "bg-white hover:border-primary"}`}>
                <Clock size={13} /> {tm}
              </button>
            ))}
          </div>
          {giroOra && (() => {
            const rows = giroRows(giroOra);
            return (
              <div data-testid="giro-list">
                <div className="text-sm font-semibold mb-2">Ore {giroOra} · {rows.length} somministrazioni</div>
                <div className="space-y-1.5">
                  {rows.length === 0 && <p className="text-sm text-muted-foreground">Nessuna somministrazione in questa fascia.</p>}
                  {rows.map(({ t, o }) => (
                    <div key={t.id} className="rounded-lg border bg-white p-2.5 flex items-start gap-3" data-testid={`giro-row-${t.id}`}>
                      <div className="text-xs font-bold bg-secondary rounded px-2 py-1 shrink-0 w-20 text-center">Cam. {o.camera || "—"}</div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm"><span className="font-semibold">{o.nome} {o.cognome}<EsBadge id={o.id} /></span> — {t.farmaco}{t.generico && <i className="text-muted-foreground"> ({t.generico})</i>}{doseGiorno(t, todayCode, giroOra) && <span className="text-muted-foreground"> · {doseGiorno(t, todayCode, giroOra)}</span>}{qtaGiorno(t, todayCode, giroOra) && <span className="text-sky-700"> · Q.tà {qtaGiorno(t, todayCode, giroOra)}</span>}</div>
                        {t.note && t.note.trim() && <div className="mt-1 flex items-start gap-1 text-[11px] text-amber-800 bg-amber-50 rounded px-1.5 py-1 whitespace-pre-wrap break-words"><StickyNote size={11} className="mt-0.5 shrink-0" /><span>{t.note}</span></div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setGiroDlg(false)}>Chiudi</Button>
            <Button onClick={() => stampaGiro(giroOra)} disabled={!giroOra} data-testid="giro-stampa-btn" className="font-semibold"><Printer size={16} className="mr-1" /> Stampa / PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
